import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';

type Theme = 'light' | 'dark';

function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/** Apply + persist an explicit theme choice. */
function applyTheme(t: Theme) {
  document.documentElement.dataset.theme = t;
  try {
    localStorage.setItem('theme', t);
  } catch {
    /* private mode: theme still applies for this page load */
  }
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', t === 'dark' ? '#0a101f' : '#edf1f7');
}

/**
 * Light/dark switch. The boot script in index.html has already applied the
 * stored (or system) theme before React mounts; this control just flips it.
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const [theme, setTheme] = useState<Theme>(() => currentTheme());

  // Follow OS changes only while the user has no explicit choice stored.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      try {
        if (localStorage.getItem('theme')) return;
      } catch {
        return;
      }
      const next: Theme = mq.matches ? 'dark' : 'light';
      document.documentElement.dataset.theme = next;
      setTheme(next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      onClick={() => {
        applyTheme(next);
        setTheme(next);
      }}
      aria-label={next === 'dark' ? 'Switch to dark theme' : 'Switch to light theme'}
      title={next === 'dark' ? 'Switch to dark theme' : 'Switch to light theme'}
      className={
        'no-print grid h-9 w-9 place-items-center rounded-md border border-line-2 bg-panel text-ink-2 transition-colors hover:text-ink ' +
        className
      }
    >
      {theme === 'dark' ? (
        <Sun className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
      ) : (
        <Moon className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
      )}
    </button>
  );
}
